/**
 * Time-zone arithmetic for personalized distribution schedules.
 *
 * Schedules are stated in the sender's IANA zone ("08:00 in America/Los_Angeles"), so every
 * send has to be turned into an instant and back without the viewer's own zone leaking in.
 * `Intl.DateTimeFormat#formatToParts` does the zone lookup; everything else is plain maths on
 * UTC milliseconds, which keeps the module pure and testable.
 */

export interface ZonedParts {
  year: number;
  /** 1–12 */
  month: number;
  day: number;
  hour: number;
  minute: number;
  /** 0 = Monday … 6 = Sunday, the contract's weekday numbering. */
  weekday: number;
}

export interface DistributionTimezoneOption {
  /** IANA name, sent as `timezone`. */
  value: string;
  /** "Pacific Time (Los Angeles)" */
  label: string;
  /** "Pacific Time" */
  short: string;
  /** "PT", shown after times. */
  abbr: string;
  /** "Pacific", used in sentences: "8:00 AM Pacific". */
  word: string;
}

/** The prototype's zones, in its order. Any other IANA name still works; it reads less nicely. */
export const DISTRIBUTION_TIMEZONES: readonly DistributionTimezoneOption[] = [
  { value: "America/Los_Angeles", label: "Pacific Time (Los Angeles)", short: "Pacific Time", abbr: "PT", word: "Pacific" },
  { value: "America/Chicago", label: "Central Time (Chicago)", short: "Central Time", abbr: "CT", word: "Central" },
  { value: "America/New_York", label: "Eastern Time (New York)", short: "Eastern Time", abbr: "ET", word: "Eastern" },
  { value: "Europe/London", label: "UK Time (London)", short: "UK Time", abbr: "UK time", word: "London time" },
  { value: "Asia/Kolkata", label: "India Time (Kolkata)", short: "India Time", abbr: "IST", word: "India time" },
];

const WEEKDAY_INDEX: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const partsFormatter = (timeZone: string): Intl.DateTimeFormat => {
  const cached = formatterCache.get(timeZone);
  if (cached) return cached;
  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  formatterCache.set(timeZone, formatter);
  return formatter;
};

/** True when the runtime knows the zone; an unknown name would make every helper throw. */
export const isKnownTimezone = (timeZone: string): boolean => {
  try {
    partsFormatter(timeZone);
    return true;
  } catch {
    return false;
  }
};

/** Wall-clock fields of an instant in `timeZone`. */
export const zonedParts = (instantMs: number, timeZone: string): ZonedParts => {
  const parts: Record<string, string> = {};
  partsFormatter(timeZone)
    .formatToParts(new Date(instantMs))
    .forEach((part) => {
      parts[part.type] = part.value;
    });
  return {
    year: Number(parts.year),
    month: Number(parts.month),
    day: Number(parts.day),
    // Some engines print midnight as "24" even with h23.
    hour: Number(parts.hour) % 24,
    minute: Number(parts.minute),
    weekday: WEEKDAY_INDEX[parts.weekday] ?? 0,
  };
};

/** Minutes the zone is ahead of UTC at that instant (−420 for 8 AM PDT). */
export const utcOffsetMinutes = (instantMs: number, timeZone: string): number => {
  const p = zonedParts(instantMs, timeZone);
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute);
  const floored = Math.floor(instantMs / 60_000) * 60_000;
  return Math.round((asUtc - floored) / 60_000);
};

/**
 * The instant at which the zone's clock reads `yyyy-mm-dd hh:mm`. A wall time skipped by a
 * spring-forward gap lands an hour later, which is what a scheduler does too.
 */
export const zonedWallTimeToInstant = (
  isoDate: string,
  time: string,
  timeZone: string,
): number => {
  const [y, m, d] = isoDate.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const first = guess - utcOffsetMinutes(guess, timeZone) * 60_000;
  const second = guess - utcOffsetMinutes(first, timeZone) * 60_000;
  return second;
};

const pad = (n: number): string => String(Math.abs(n)).padStart(2, "0");

/** ISO-8601 with the zone's own offset: "2026-10-20T08:00:00-07:00". */
export const toIsoWithOffset = (instantMs: number, timeZone: string): string => {
  const offset = utcOffsetMinutes(instantMs, timeZone);
  const local = new Date(instantMs + offset * 60_000).toISOString().slice(0, 19);
  const sign = offset < 0 ? "-" : "+";
  return `${local}${sign}${pad(Math.trunc(offset / 60))}:${pad(offset % 60)}`;
};

/** The zone's calendar date of an instant, as yyyy-mm-dd. */
export const zonedIsoDate = (instantMs: number, timeZone: string): string => {
  const p = zonedParts(instantMs, timeZone);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
};

/** The zone as the runtime names it, so an alias (`Asia/Calcutta`) matches its zone (`Asia/Kolkata`). */
const canonicalZone = (timeZone: string): string => {
  try {
    return new Intl.DateTimeFormat("en-US", { timeZone }).resolvedOptions().timeZone;
  } catch {
    return timeZone;
  }
};

const knownZone = (timeZone: string): DistributionTimezoneOption | undefined =>
  DISTRIBUTION_TIMEZONES.find((zone) => zone.value === timeZone) ??
  DISTRIBUTION_TIMEZONES.find((zone) => canonicalZone(zone.value) === canonicalZone(timeZone));

/** The prototype's labels for a known zone, or labels built from the IANA name. */
export const timezoneOption = (timeZone: string): DistributionTimezoneOption => {
  const known = knownZone(timeZone);
  if (known) return known;
  const city = (timeZone.split("/").pop() ?? timeZone).replace(/_/g, " ");
  return { value: timeZone, label: timeZone, short: city, abbr: city, word: `${city} time` };
};

// ─── Friendly labels (Simple "When" section) ──────────────────────────────────

/** "Pacific Time (Los Angeles)" for a known zone; "Berlin (Europe)" for any other IANA name. */
export const friendlyTimezoneLabel = (timeZone: string): string => {
  const known = knownZone(timeZone);
  if (known) return known.label;
  const [region, ...rest] = timeZone.split("/");
  if (rest.length === 0) return timeZone;
  return `${(rest[rest.length - 1] ?? "").replace(/_/g, " ")} (${region.replace(/_/g, " ")})`;
};

/** "PST" / "PDT" at that instant: the runtime's own short name, else the zone's abbreviation ("IST"). */
export const zoneAbbreviationAt = (instantMs: number, timeZone: string): string => {
  if (!isKnownTimezone(timeZone)) return timezoneOption(timeZone).abbr;
  const name = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "short" })
    .formatToParts(new Date(instantMs))
    .find((part) => part.type === "timeZoneName")?.value;
  return name && /^[A-Z]{2,5}$/.test(name) ? name : timezoneOption(timeZone).abbr;
};

const SHORT_WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export interface SendInstantLabel {
  /** "Mon, Nov 2" */
  date: string;
  /** "9:00 AM" */
  time: string;
  /** "PST" */
  zone: string;
}

/** A send's date, clock time and zone abbreviation as the zone's own clock reads them. */
export const sendInstantLabel = (iso: string, timeZone: string): SendInstantLabel => {
  const ms = Date.parse(iso);
  const zone = isKnownTimezone(timeZone) ? timeZone : "UTC";
  const p = zonedParts(ms, zone);
  const hour12 = p.hour % 12 || 12;
  return {
    date: `${SHORT_WEEKDAYS[p.weekday]}, ${SHORT_MONTHS[p.month - 1]} ${p.day}`,
    time: `${hour12}:${String(p.minute).padStart(2, "0")} ${p.hour < 12 ? "AM" : "PM"}`,
    zone: zoneAbbreviationAt(ms, zone),
  };
};
